import { ArrowRight, ArrowUpRight } from "lucide-react";

const IMG = "/assets/fin-reco-demo.png";
const STAGES = ["Attract & Hire", "Onboard", "Serve", "Develop & Retain", "Offboard"];
const DESC = "Transform the complete employee lifecycle with AI agents working across every stage of the employee journey.";

const Thumb = () => (
  <div className="relative rounded-xl overflow-hidden border border-[#3E2818]/12 shadow-[0_18px_48px_-30px_rgba(62,40,24,0.5)]">
    <div className="flex items-center gap-2 px-3.5 py-2.5 bg-[#F1EDE6] border-b border-[#3E2818]/8">
      <span className="w-2.5 h-2.5 rounded-full bg-[#E88B6B]" />
      <span className="w-2.5 h-2.5 rounded-full bg-[#E8C15E]" />
      <span className="w-2.5 h-2.5 rounded-full bg-[#9CB88A]" />
      <span className="ml-2 flex-1 truncate rounded-md bg-white/70 px-2.5 py-1 font-mono2 text-[10px] text-[#3E2818]/55">fin-reco.demo.intugle.ai</span>
    </div>
    <div className="relative aspect-[16/11] overflow-hidden">
      <img src={IMG} alt="demo" className="absolute inset-0 w-full h-full object-cover object-top" />
      <div className="absolute inset-x-0 bottom-0 h-2/5 bg-gradient-to-t from-[#211307]/85 via-[#211307]/45 to-transparent" />
      <div className="absolute inset-x-0 bottom-0 p-4 flex items-end justify-between">
        <div>
          <p className="font-mono2 text-[10px] uppercase tracking-[0.22em] text-[#E8AA68]">Finance Close</p>
          <p className="text-[15px] font-semibold text-white mt-0.5">Explore the live demo</p>
        </div>
        <span className="w-9 h-9 rounded-full bg-white text-[#211307] flex items-center justify-center shrink-0"><ArrowUpRight size={18} strokeWidth={2.4} /></span>
      </div>
    </div>
  </div>
);

const Pills = ({ pillClass = "bg-[#211307]/6", numClass = "text-[#B87A32]", textClass = "text-[#3E2818]", arrowClass = "text-[#B87A32]" }) => (
  <div className="flex flex-wrap items-center gap-2.5">
    {STAGES.map((s, i) => (
      <div key={s} className="flex items-center gap-1.5">
        <span className={`flex items-center gap-2 rounded-full px-3.5 py-2 ${pillClass}`}>
          <span className={`font-mono2 text-[10px] ${numClass}`}>{String(i + 1).padStart(2, "0")}</span>
          <span className={`text-[12.5px] font-semibold ${textClass}`}>{s}</span>
        </span>
        {i < STAGES.length - 1 && <ArrowRight size={14} className={arrowClass} />}
      </div>
    ))}
  </div>
);

/* G · No card — inline on cream with top hairline */
const G = () => (
  <div className="mt-1 border-t border-[#3E2818]/12 pt-6">
    <p className="font-mono2 text-[10px] uppercase tracking-[0.22em] text-[#8A5730] mb-2">Overall use case</p>
    <h4 className="font-display text-xl font-medium text-[#3E2818]">Hire-to-Retire</h4>
    <p className="text-[13px] text-[#3E2818]/60 mt-2 mb-5 max-w-2xl leading-relaxed">{DESC}</p>
    <Pills />
  </div>
);

/* H · Ghost outline (transparent) */
const H = () => (
  <div className="mt-1 rounded-2xl border border-[#8A5730]/35 p-5 lg:p-6">
    <h4 className="font-display text-xl font-medium text-[#3E2818]">Hire-to-Retire</h4>
    <p className="text-[13px] text-[#3E2818]/60 mt-2 mb-5 max-w-2xl leading-relaxed">{DESC}</p>
    <Pills pillClass="bg-white border border-[#3E2818]/10" />
  </div>
);

/* I · Sage-tinted panel */
const I = () => (
  <div className="mt-1 rounded-2xl bg-[#E7EBDF] border border-[#9CA888]/40 p-5 lg:p-6">
    <h4 className="font-display text-xl font-medium text-[#2F3A22]">Hire-to-Retire</h4>
    <p className="text-[13px] text-[#3E4A2C] mt-2 mb-5 max-w-2xl leading-relaxed">{DESC}</p>
    <Pills pillClass="bg-white/70" numClass="text-[#6C7A4E]" textClass="text-[#2F3A22]" arrowClass="text-[#6C7A4E]" />
  </div>
);

/* J · Two-tone split — dark header, light body */
const J = () => (
  <div className="mt-1 rounded-2xl overflow-hidden border border-[#3E2818]/10">
    <div className="bg-[#211307] px-5 lg:px-6 py-4">
      <p className="font-mono2 text-[10px] uppercase tracking-[0.22em] text-[#E8AA68]">Overall use case</p>
      <h4 className="font-display text-xl font-medium text-[#F7F5F0] mt-1">Hire-to-Retire</h4>
    </div>
    <div className="bg-[#FBF8F2] px-5 lg:px-6 py-5">
      <p className="text-[13px] text-[#3E2818]/60 mb-5 max-w-2xl leading-relaxed">{DESC}</p>
      <Pills />
    </div>
  </div>
);

/* K · Dot-grid textured cream */
const K = () => (
  <div
    className="mt-1 rounded-2xl border border-[#8A5730]/20 p-5 lg:p-6 bg-[#FBF5EA]"
    style={{ backgroundImage: "radial-gradient(rgba(138,87,48,0.14) 1px, transparent 1px)", backgroundSize: "14px 14px" }}
  >
    <h4 className="font-display text-xl font-medium text-[#3E2818]">Hire-to-Retire</h4>
    <p className="text-[13px] text-[#3E2818]/60 mt-2 mb-5 max-w-2xl leading-relaxed">{DESC}</p>
    <Pills pillClass="bg-white border border-[#3E2818]/8" />
  </div>
);

/* L · Left gold rail on cream */
const L = () => (
  <div className="mt-1 rounded-2xl bg-[#FBF2E1] p-5 lg:p-6 border-l-[3px] border-[#EDA13A]">
    <p className="font-mono2 text-[10px] uppercase tracking-[0.22em] text-[#B87A32] mb-2">Overall use case</p>
    <h4 className="font-display text-xl font-medium text-[#3E2818]">Hire-to-Retire</h4>
    <p className="text-[13px] text-[#3E2818]/60 mt-2 mb-5 max-w-2xl leading-relaxed">{DESC}</p>
    <Pills pillClass="bg-white" />
  </div>
);

const OPTIONS = [
  { id: "g", title: "Option G · No Card, Inline on Cream", note: "Remove the block entirely — a hairline divider, kicker label, heading, and pills sit directly on the section background. The thumbnail becomes the only 'contained' element. Cleanest.", comp: G },
  { id: "h", title: "Option H · Ghost Outline", note: "Just a thin caramel outline, transparent inside. Architectural and light — no dark mass to fight the thumbnail.", comp: H },
  { id: "i", title: "Option I · Sage-Tinted Panel", note: "A soft muted-sage panel from the brand palette — a different hue entirely, so no brown-on-brown clash with the thumbnail.", comp: I },
  { id: "j", title: "Option J · Two-Tone Split", note: "Small dark espresso header holds the title; the body is light. Keeps a touch of dark but breaks up the mass so it reads distinct from the thumbnail.", comp: J },
  { id: "k", title: "Option K · Dot-Grid Texture", note: "Cream panel with a subtle caramel dot grid — adds quiet texture and warmth without a heavy dark fill.", comp: K },
  { id: "l", title: "Option L · Gold Left Rail", note: "Cream-gold panel with a single gold accent rail on the left edge. Warm, minimal, clearly its own element.", comp: L },
];

export default function HireBlockOptions2() {
  return (
    <div className="min-h-screen bg-[#F7F5F0]">
      <div className="max-w-6xl mx-auto px-6 py-12">
        <p className="font-mono2 text-[10px] uppercase tracking-[0.3em] text-[#8A5730]">Hire-to-Retire Block · More Options</p>
        <h1 className="font-display text-2xl lg:text-3xl font-medium mt-2 tracking-tight text-[#3E2818]">A fresh set of background directions</h1>
        <p className="text-sm text-[#3E2818]/60 mt-3 max-w-2xl leading-relaxed">Each is paired with the real demo thumbnail so you can see how they sit together. Tell me the letter and I&rsquo;ll apply it.</p>

        <div className="mt-10 space-y-14">
          {OPTIONS.map((o) => {
            const C = o.comp;
            return (
              <div key={o.id}>
                <p className="font-mono2 text-[11px] uppercase tracking-[0.2em] text-[#E8AA68] mb-3">{o.title}</p>
                <div className="grid lg:grid-cols-[1fr_0.55fr] gap-6 lg:gap-10 items-start">
                  <C />
                  <Thumb />
                </div>
                <p className="text-[12px] text-[#3E2818]/55 mt-3 leading-snug max-w-3xl">{o.note}</p>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}
